import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalyticsEventLocation } from '@mb/lib';
import { IconType } from 'components/CustomIcon/CustomIcon.config';
import { IntlShape } from 'react-intl';
import * as Styled from './AskProductOrderItem.styles';
import { saveContactRepMethod } from '../../api/contactRepMethod';
import { MessageData, sendMessage } from '../../api/messages';
import { definedTranslation } from '../../i18n';
import {
  ContactRepMethod,
  IProduct,
  IProject,
  Representatives,
} from '../../types';
import { getFormattedDate } from '../../utils/getFormattedDate';
import {
  AnalyticsEventAPQAction,
  AnalyticsEventContactType,
  AnalyticsEventName,
  sendAnalyticsEvent,
} from '../../utils/helpers/analytics';
import { getUserAttributes } from '../../utils/localStorage';
import { AskProductOrderDetails } from '../AskProductOrderDetails';
import { AskProductOrderDetailsToggle } from '../AskProductOrderDetailsToggle';

export interface AskProductOrderItemProps {
  itemID: string;
  brand: string;
  products: IProduct[];
  project: IProject;
  orderID: string;
  orderDate: string;
  onActionClick: (itemID: string) => void;
  representatives: Representatives;
  intl: IntlShape;
}

export const AskProductOrderItem: React.FC<AskProductOrderItemProps> = (
  props,
) => {
  const {
    itemID,
    orderID,
    orderDate,
    brand,
    products,
    project,
    onActionClick,
    representatives,
    intl,
  } = props;
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState<boolean>(false);
  const [userContactedRep, setUserContactedRep] = useState<
    ContactRepMethod | undefined
  >(undefined);
  const [isReacted, setIsReacted] = useState<boolean>(false);
  const [selectedRepresentative, setSelectedRepresentative] =
    useState<string>('');

  const labels = {
    readyToSpecify: {
      label: intl?.formatMessage(
        definedTranslation.repMethods.readyToSpecify.label,
      ),
      responseText: intl?.formatMessage(
        definedTranslation.repMethods.readyToSpecify.responseText,
      ),
    },
    requestPricing: {
      label: intl?.formatMessage(
        definedTranslation.repMethods.requestPricing.label,
      ),
      responseText: intl?.formatMessage(
        definedTranslation.repMethods.requestPricing.responseText,
      ),
    },
    requestAvailability: {
      label: intl?.formatMessage(
        definedTranslation.repMethods.requestAvailability.label,
      ),
      responseText: intl?.formatMessage(
        definedTranslation.repMethods.requestAvailability.responseText,
      ),
    },
    requestLeadTime: {
      label: intl?.formatMessage(
        definedTranslation.repMethods.requestLeadTime.label,
      ),
      responseText: intl?.formatMessage(
        definedTranslation.repMethods.requestLeadTime.responseText,
      ),
    },
    inbox: intl?.formatMessage(definedTranslation.inboxLabel),
    okForNow: intl?.formatMessage(definedTranslation.repMethods.okForNow),
    messageSent: intl?.formatMessage(definedTranslation.repMethods.messageSent),
    hideDetails: intl?.formatMessage(
      definedTranslation.items.hideDetailsButton,
    ),
    rep: intl?.formatMessage(definedTranslation.items.repLabel),
    orderDate: intl?.formatMessage(definedTranslation.orderDate),
  };

  const defaultMessages = {
    [ContactRepMethod.READY_TO_SPECIFY]: intl?.formatMessage(
      definedTranslation.repMethods.readyToSpecify.message,
    ),
    [ContactRepMethod.REQUEST_PRICING]: intl?.formatMessage(
      definedTranslation.repMethods.requestPricing.message,
    ),
    [ContactRepMethod.REQUEST_AVAILABILITY]: intl?.formatMessage(
      definedTranslation.repMethods.requestAvailability.message,
    ),
    [ContactRepMethod.REQUEST_LEAD_TIME]: intl?.formatMessage(
      definedTranslation.repMethods.requestLeadTime.message,
    ),
  };

  const handleToggleOrderDetails = useCallback(() => {
    setIsOrderDetailsOpen((prevState) => {
      sendAnalyticsEvent({
        event: AnalyticsEventName.APQ,
        event_location: AnalyticsEventLocation.HOME,
        action: prevState
          ? AnalyticsEventAPQAction.COLLAPSE_BRAND_SAMPLES
          : AnalyticsEventAPQAction.EXPAND_BRAND_SAMPLES,
      });
      return !prevState;
    });
  }, []);

  const representativeData = useMemo(() => {
    const assignedRep = representatives.assignedRep;
    const assignableReps = representatives.assignableReps;

    if (assignedRep) {
      return {
        name: assignedRep.first_name + ' ' + assignedRep.last_name,
        id: assignedRep.id,
      };
    } else if (assignableReps && assignableReps.length > 0) {
      return {
        name: assignableReps[0].first_name + ' ' + assignableReps[0].last_name,
        id: assignableReps[0].id,
      };
    }
    return { name: '', id: '' };
  }, [representatives]);

  useEffect(() => {
    setSelectedRepresentative(representativeData.name);
  }, [representativeData]);

  const handleMessageSent = useCallback(
    (contactRepMethod: ContactRepMethod) => {
      const isDismissEvent =
        contactRepMethod === ContactRepMethod.DISMISS_BRAND;
      const eventName = isDismissEvent
        ? AnalyticsEventName.APQ
        : AnalyticsEventName.CONTACT_REP;
      const eventLocation = isDismissEvent
        ? AnalyticsEventLocation.HOME
        : AnalyticsEventLocation.APQ_MODAL;
      const actionName = isDismissEvent
        ? AnalyticsEventAPQAction.DISMISS_BRAND
        : AnalyticsEventAPQAction.SEND_MESSAGE;
      const typeName = isDismissEvent
        ? undefined
        : getTypeName(contactRepMethod);

      setIsReacted(true);
      setUserContactedRep(contactRepMethod);
      setIsOrderDetailsOpen(false);
      sendAnalyticsEvent({
        event: eventName,
        event_location: eventLocation,
        action: actionName,
        type: typeName,
      });

      onActionClick(itemID);
    },
    [],
  );

  const handleActionClick = useCallback(
    (contactRepMethod: ContactRepMethod) => {
      if (isReacted) {
        return;
      }

      const contactRepMethodData = {
        customer_id: getUserAttributes().customer_id,
        order_id: orderID,
        brand_id: products[0].manufacturer_id,
        option: contactRepMethod,
        project_id: project.id,
      };

      if (
        contactRepMethod === ContactRepMethod.READY_TO_SPECIFY ||
        contactRepMethod === ContactRepMethod.REQUEST_PRICING ||
        contactRepMethod === ContactRepMethod.REQUEST_AVAILABILITY ||
        contactRepMethod === ContactRepMethod.REQUEST_LEAD_TIME
      ) {
        const product_ids = products.map((product) => product.id);
        const unique_product_ids = product_ids.filter(
          (value, index, self) => self.indexOf(value) === index,
        );
        const product_name = products.map((product) => product.product_name);
        const unique_product_name = product_name.filter(
          (value, index, self) => self.indexOf(value) === index,
        );

        const data: MessageData = {
          product_id: unique_product_ids.join(','),
          rep_id: representativeData.id,
          project_id: project.id,
          message: defaultMessages[contactRepMethod],
          message_source: 'ask_product_modal',
          manufacturer_sku: products[0].manufacturer_sku,
          manufacturer: brand,
          quick_topics: [],
          product_name: unique_product_name.join(','),
        };
        void sendMessage(data);
      }

      // COMMON ACTIONS AFTER MESSAGE SENT
      handleMessageSent(contactRepMethod);
      void saveContactRepMethod(contactRepMethodData);
    },
    [isReacted, representativeData, products, project, brand, itemID],
  );

  const getRepContactMethodData = useCallback(
    (repContactMethod: ContactRepMethod) => {
      let icon: IconType = 'thumb-up';
      let text = labels.okForNow;
      const sentText = labels.messageSent;
      let link = null;
      const InboxLink = (
        <Styled.InboxLink href="/inbox" target="_blank">
          {labels.inbox}
        </Styled.InboxLink>
      );

      switch (repContactMethod) {
        case ContactRepMethod.READY_TO_SPECIFY:
          icon = 'file';
          text = `${sentText} ${selectedRepresentative} ${labels.readyToSpecify.responseText}`;
          link = InboxLink;
          break;
        case ContactRepMethod.REQUEST_PRICING:
          icon = 'price';
          text = `${sentText} ${selectedRepresentative} ${labels.requestPricing.responseText}`;
          link = InboxLink;
          break;
        case ContactRepMethod.REQUEST_AVAILABILITY:
          icon = 'cart-plus';
          text = `${sentText} ${selectedRepresentative} ${labels.requestAvailability.responseText}`;
          link = InboxLink;
          break;
        case ContactRepMethod.REQUEST_LEAD_TIME:
          icon = 'clock';
          text = `${sentText} ${selectedRepresentative} ${labels.requestLeadTime.responseText}`;
          link = InboxLink;
          break;
      }

      return { icon, text, link };
    },
    [selectedRepresentative],
  );

  const getTypeName = useCallback(
    (contactRepMethod: ContactRepMethod): AnalyticsEventContactType => {
      switch (contactRepMethod) {
        case ContactRepMethod.DISMISS_BRAND:
          return AnalyticsEventContactType.DISMISS_BRAND;
        case ContactRepMethod.READY_TO_SPECIFY:
          return AnalyticsEventContactType.READY_TO_SPECIFY;
        case ContactRepMethod.REQUEST_PRICING:
          return AnalyticsEventContactType.REQUEST_PRICING;
        case ContactRepMethod.REQUEST_AVAILABILITY:
          return AnalyticsEventContactType.REQUEST_PRODUCT_AVAILABILITY;
        case ContactRepMethod.REQUEST_LEAD_TIME:
          return AnalyticsEventContactType.REQUEST_LEAD_TIME;
        default:
          return AnalyticsEventContactType.DISMISS_BRAND;
      }
    },
    [],
  );

  return (
    <Styled.Item hide={!!userContactedRep}>
      {/* @ts-expect-error // TODO: fix types */}
      <Styled.OrderCard>
        {!userContactedRep && (
          <Styled.CloseButton
            onClick={() => handleActionClick(ContactRepMethod.DISMISS_BRAND)}
            dense
            color="inherit"
            iconName="close"
          />
        )}
        <Styled.OrderCardWrapper>
          <Styled.OrderCardInfo>
            <Styled.OrderCardBrandName variant="body" fontWeight={600}>
              {brand}
            </Styled.OrderCardBrandName>
            <Styled.OrderCardDescription variant="tiny" fontWeight={400}>
              <span>{labels.rep}:&nbsp;</span>
              {selectedRepresentative || representativeData.name}
            </Styled.OrderCardDescription>
            <Styled.OrderCardDescription variant="tiny" fontWeight={400}>
              <span>{labels.orderDate}:&nbsp;</span>
              {getFormattedDate(orderDate)}
            </Styled.OrderCardDescription>

            <AskProductOrderDetailsToggle
              isOrderDetailsOpen={isOrderDetailsOpen}
              onToggleClick={handleToggleOrderDetails}
              products={products}
              hideDetailsLabel={labels.hideDetails}
            />
          </Styled.OrderCardInfo>

          {!userContactedRep && (
            <>
              <Styled.OrderCardActions>
                {/* @ts-expect-error // TODO: fix types */}
                <Styled.OrderCardAction
                  onClick={() =>
                    handleActionClick(ContactRepMethod.READY_TO_SPECIFY)
                  }
                >
                  <Styled.ActionIcon iconName="file-plus" size="large" />
                  <Styled.ActionText variant="tiny" fontWeight={400}>
                    {labels.readyToSpecify.label}
                  </Styled.ActionText>
                </Styled.OrderCardAction>
                {/* @ts-expect-error // TODO: fix types */}
                <Styled.OrderCardAction
                  onClick={() =>
                    handleActionClick(ContactRepMethod.REQUEST_PRICING)
                  }
                >
                  <Styled.ActionIcon iconName="price" size="large" />
                  <Styled.ActionText variant="tiny" fontWeight={400}>
                    {labels.requestPricing.label}
                  </Styled.ActionText>
                </Styled.OrderCardAction>
                {/* @ts-expect-error // TODO: fix types */}
                <Styled.OrderCardAction
                  onClick={() =>
                    handleActionClick(ContactRepMethod.REQUEST_AVAILABILITY)
                  }
                >
                  <Styled.ActionIcon iconName="cart-plus" size="large" />
                  <Styled.ActionText variant="tiny" fontWeight={400}>
                    {labels.requestAvailability.label}
                  </Styled.ActionText>
                </Styled.OrderCardAction>
                {/* @ts-expect-error // TODO: fix types */}
                <Styled.OrderCardAction
                  onClick={() =>
                    handleActionClick(ContactRepMethod.REQUEST_LEAD_TIME)
                  }
                >
                  <Styled.ActionIcon iconName="clock" size="large" />
                  <Styled.ActionText variant="tiny" fontWeight={400}>
                    {labels.requestLeadTime.label}
                  </Styled.ActionText>
                </Styled.OrderCardAction>
              </Styled.OrderCardActions>
            </>
          )}
          {userContactedRep && (
            // @ts-expect-error // TODO: fix types
            <Styled.OrderCardUserContactedRep>
              <Styled.ActionIcon
                iconName={getRepContactMethodData(userContactedRep).icon}
                size="large"
              />
              <Styled.OrderCardUserContactedRepText
                variant="tiny"
                fontWeight={400}
              >
                {getRepContactMethodData(userContactedRep).text}{' '}
                {getRepContactMethodData(userContactedRep).link}
              </Styled.OrderCardUserContactedRepText>
            </Styled.OrderCardUserContactedRep>
          )}
        </Styled.OrderCardWrapper>

        <AskProductOrderDetails
          isOrderDetailsOpen={isOrderDetailsOpen}
          products={products}
        />
      </Styled.OrderCard>
    </Styled.Item>
  );
};
